import { Portal } from '@angular/cdk/portal';
import { Injectable, signal } from '@angular/core';
import { StorageFile } from '@lib/models/generated/graphql';
import { OuiSlidePanelService } from 'omnium-ui/layout';
import { BehaviorSubject, Observable, filter, map, tap } from 'rxjs';

export enum RecueilTypePreference {
  couple = 'couple',
  seule = 'seule',
}

export interface RecueilDocumentViewerData {
  recueilTypePreference: RecueilTypePreference;
  coupleFileMetadata?: StorageFile;
  splittedFileMetadatas?: [StorageFile, StorageFile];
}

/** Options d'ouverture communes aux panneaux de prévisualisation des documents de connaissance client (DER, QPI, recueil). */
export interface DocumentViewerOpenOptions {
  /** Affiche le bouton « Envoyer en signature » (true par défaut). Désactivé lorsque le panneau est ouvert depuis une opération. */
  showSendToSignature?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DocumentViewerPanelService {
  public fileMetadataSubject = new BehaviorSubject<StorageFile | null>(null);
  public recueilTypePreferenceSubject = new BehaviorSubject<RecueilTypePreference | null>(null);
  public recueilDocumentViewerDataSubject = new BehaviorSubject<RecueilDocumentViewerData | null>(null);
  public readonly showSendToSignature = signal<boolean>(true);

  public readonly viewerClosed$: Observable<void>;

  private opened: boolean = false;

  private documentViewerPortal: Portal<any> | null = null;

  constructor(private slidePanelService: OuiSlidePanelService) {
    this.viewerClosed$ = this.slidePanelService.slidePanelState$.pipe(
      filter(state => state === 'closed' && this.opened),
      tap(() => this.onPanelClose()),
      map(() => {})
    );
  }

  public registerPortal(portal: Portal<any>) {
    this.documentViewerPortal = portal;
  }

  public openRecueilDocumentViewer(
    recueilTypePreference: RecueilTypePreference,
    coupleFileMetadata?: StorageFile,
    splittedFileMetadatas?: [StorageFile, StorageFile],
    options: DocumentViewerOpenOptions = {}
  ) {
    if (!this.documentViewerPortal) {
      throw new Error(`No portal registered in document viewer service. Please register a portal using registerPortal.`);
    }
    this.showSendToSignature.set(options.showSendToSignature ?? true);
    this.fileMetadataSubject.next(coupleFileMetadata ?? splittedFileMetadatas?.[0] ?? null);
    this.recueilTypePreferenceSubject.next(recueilTypePreference);
    this.recueilDocumentViewerDataSubject.next({ recueilTypePreference, coupleFileMetadata, splittedFileMetadatas });
    this.slidePanelService.setSlidePanelContent(this.documentViewerPortal);
    this.slidePanelService.openSlidePanel({ position: 'right', autoFocus: false, disableClose: false });

    this.opened = true;
  }

  public closeDocumentViewer() {
    this.slidePanelService.setSlidePanelContent(null);
    this.slidePanelService.closeSlidePanel();
  }

  private onPanelClose() {
    this.opened = false;
    this.fileMetadataSubject.next(null);
    this.recueilTypePreferenceSubject.next(null);
    this.recueilDocumentViewerDataSubject.next(null);
  }
}
